/**
 * payrollCalculations.js
 * Pure, side-effect-free calculation functions for the org-wide payroll system.
 */

// ---------------------------------------------------------------------------
// Internal helper — topological sort (Kahn's algorithm)
// ---------------------------------------------------------------------------

function topologicalSort(components) {
  if (!components || components.length === 0) return [];

  const idToComponent = new Map(components.map((c) => [c.id, c]));
  const inDegree = new Map(components.map((c) => [c.id, 0]));
  const dependents = new Map(components.map((c) => [c.id, []]));

  for (const c of components) {
    const isDependent =
      (c.calculationType === "percentage" || c.calculationType === "percentage_of_component") &&
      c.baseComponentId &&
      idToComponent.has(c.baseComponentId);
    if (isDependent) {
      inDegree.set(c.id, (inDegree.get(c.id) || 0) + 1);
      dependents.get(c.baseComponentId).push(c.id);
    }
  }

  const queue = components.filter((c) => inDegree.get(c.id) === 0);
  const sorted = [];

  while (queue.length > 0) {
    const node = queue.shift();
    sorted.push(node);
    for (const depId of dependents.get(node.id) || []) {
      const newDegree = (inDegree.get(depId) || 0) - 1;
      inDegree.set(depId, newDegree);
      if (newDegree === 0) queue.push(idToComponent.get(depId));
    }
  }

  return sorted.length < components.length ? [...components] : sorted;
}

// ---------------------------------------------------------------------------
// resolveComponents — legacy (uses sampleAmount fallback)
// ---------------------------------------------------------------------------

export function resolveComponents(components, sampleAmount) {
  if (!components || components.length === 0) return new Map();

  const sorted = topologicalSort(components);
  const resolved = new Map();

  let fallbackId = null;
  const basicComp = sorted.find((c) => c.name && c.name.toLowerCase() === "basic");
  if (basicComp) {
    fallbackId = basicComp.id;
  } else {
    const firstFixed = sorted.find((c) => c.calculationType === "fixed");
    if (firstFixed) fallbackId = firstFixed.id;
  }

  for (const comp of sorted) {
    if (comp.calculationType === "residual" || comp.isResidual) {
      resolved.set(comp.id, 0);
      continue;
    }
    if (comp.calculationType === "fixed") {
      const amount = Number(comp.fixedAmount) || 0;
      resolved.set(comp.id, amount);
      if (comp.id === fallbackId && sampleAmount != null && sampleAmount > 0) {
        resolved.set(comp.id, Number(sampleAmount));
      }
    } else if (comp.calculationType === "percentage") {
      const pct = Number(comp.percentage) || 0;
      let base = 0;
      if (comp.baseComponentId && resolved.has(comp.baseComponentId)) {
        base = resolved.get(comp.baseComponentId);
      } else if (sampleAmount != null && sampleAmount > 0) {
        base = Number(sampleAmount);
      }
      resolved.set(comp.id, (pct / 100) * base);
    } else if (
      comp.calculationType === "percentage_of_gross" ||
      comp.calculationType === "percentage_of_ctc"
    ) {
      const pct = Number(comp.percentage) || 0;
      const base = sampleAmount != null ? Number(sampleAmount) : 0;
      resolved.set(comp.id, (pct / 100) * base);
    } else if (comp.calculationType === "percentage_of_component") {
      const pct = Number(comp.percentage) || 0;
      const base = comp.baseComponentId ? resolved.get(comp.baseComponentId) || 0 : 0;
      resolved.set(comp.id, (pct / 100) * base);
    } else {
      resolved.set(comp.id, 0);
    }
  }

  // Resolve residual
  const residualComp = sorted.find((c) => c.calculationType === "residual" || c.isResidual);
  if (residualComp) {
    const nonResidualSum = sorted
      .filter((c) => c.id !== residualComp.id)
      .reduce((sum, c) => sum + (resolved.get(c.id) || 0), 0);
    const base = sampleAmount != null ? Number(sampleAmount) : 0;
    resolved.set(residualComp.id, Math.max(0, base - nonResidualSum));
  }

  return resolved;
}

// ---------------------------------------------------------------------------
// resolveComponentsWithSalary — org-wide model
// ---------------------------------------------------------------------------

export function resolveComponentsWithSalary(components, monthlySalary) {
  if (!components || components.length === 0) return new Map();

  const salary = Number(monthlySalary) || 0;
  const sorted = topologicalSort(components);
  const resolved = new Map();

  for (const comp of sorted) {
    if (comp.calculationType === "residual" || comp.isResidual) {
      resolved.set(comp.id, 0);
      continue;
    }
    let amount = 0;
    switch (comp.calculationType) {
      case "fixed":
        amount = Number(comp.fixedAmount) || 0;
        break;
      case "percentage_of_gross":
      case "percentage_of_ctc":
        amount = Math.round(((Number(comp.percentage) || 0) / 100) * salary);
        break;
      case "percentage_of_component":
      case "percentage": {
        const pct = Number(comp.percentage) || 0;
        const base = comp.baseComponentId ? resolved.get(comp.baseComponentId) || 0 : salary;
        amount = Math.round((pct / 100) * base);
        break;
      }
      default:
        amount = 0;
    }
    resolved.set(comp.id, amount);
  }

  // Resolve residual = salary - sum(all non-residual)
  const residualComp = sorted.find((c) => c.calculationType === "residual" || c.isResidual);
  if (residualComp) {
    const nonResidualSum = sorted
      .filter((c) => c.id !== residualComp.id)
      .reduce((sum, c) => sum + (resolved.get(c.id) || 0), 0);
    resolved.set(residualComp.id, Math.max(0, salary - nonResidualSum));
  }

  return resolved;
}

// ---------------------------------------------------------------------------
// computeGross
// ---------------------------------------------------------------------------

export function computeGross(components, sampleAmount) {
  if (!components || components.length === 0) return 0;
  const resolved = resolveComponents(components, sampleAmount);
  let total = 0;
  for (const amount of resolved.values()) total += amount;
  return total;
}

// ---------------------------------------------------------------------------
// detectCycle
// ---------------------------------------------------------------------------

export function detectCycle(components) {
  if (!components || components.length === 0) return false;

  const idSet = new Set(components.map((c) => c.id));
  const adj = new Map(components.map((c) => [c.id, []]));

  for (const c of components) {
    const isDependent =
      (c.calculationType === "percentage" || c.calculationType === "percentage_of_component") &&
      c.baseComponentId &&
      idSet.has(c.baseComponentId);
    if (isDependent) adj.get(c.id).push(c.baseComponentId);
  }

  const state = new Map(components.map((c) => [c.id, 0]));

  function dfs(nodeId) {
    state.set(nodeId, 1);
    for (const neighbour of adj.get(nodeId) || []) {
      const s = state.get(neighbour);
      if (s === 1) return true;
      if (s === 0 && dfs(neighbour)) return true;
    }
    state.set(nodeId, 2);
    return false;
  }

  for (const c of components) {
    if (state.get(c.id) === 0 && dfs(c.id)) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// computeLop
// ---------------------------------------------------------------------------

export function computeLop(gross, workingDays, absentDays) {
  if (!absentDays || absentDays === 0) return 0;
  if (!workingDays || workingDays === 0) return 0;
  return Math.round((gross / workingDays) * absentDays);
}

// ---------------------------------------------------------------------------
// computeProrata
// ---------------------------------------------------------------------------

export function computeProrata(amount, presentDays, monthDays, isProrata) {
  if (!isProrata) return Math.round(amount);
  if (!monthDays || monthDays === 0) return 0;
  return Math.round((amount / monthDays) * presentDays);
}

// ---------------------------------------------------------------------------
// computePf
// ---------------------------------------------------------------------------

export function computePf(basicAmount, employeePfPct, employerPfPct, pfCeiling = 15000) {
  const basic = Math.min(Number(basicAmount) || 0, Number(pfCeiling) || 15000);
  return {
    employee: Math.round((basic * (Number(employeePfPct) || 0)) / 100),
    employer: Math.round((basic * (Number(employerPfPct) || 0)) / 100),
  };
}

// ---------------------------------------------------------------------------
// computeEsic
// ---------------------------------------------------------------------------

export function computeEsic(gross, ceiling, employeePct) {
  const g = Number(gross) || 0;
  if (g > (Number(ceiling) || 0)) return 0;
  return Math.round((g * (Number(employeePct) || 0)) / 100);
}

// ---------------------------------------------------------------------------
// computePt
// ---------------------------------------------------------------------------

export function computePt(gross, state, ptSlabTable) {
  if (!state || !ptSlabTable || ptSlabTable.length === 0) return 0;
  const g = Number(gross) || 0;
  const norm = state.trim().toLowerCase();
  const stateSlabs = ptSlabTable.filter((s) => s.state && s.state.trim().toLowerCase() === norm);
  if (stateSlabs.length === 0) return 0;
  for (const slab of stateSlabs) {
    const from = Number(slab.salaryFrom) || 0;
    const to = slab.salaryTo == null ? Infinity : Number(slab.salaryTo);
    if (g >= from && g <= to) return Number(slab.ptAmount) || 0;
  }
  return 0;
}

// ---------------------------------------------------------------------------
// computeAnnualTax
// ---------------------------------------------------------------------------

export function computeAnnualTax(annualIncome, tdsSlabTable) {
  if (!tdsSlabTable || tdsSlabTable.length === 0) return 0;
  const income = Number(annualIncome) || 0;
  let totalTax = 0;
  for (const slab of tdsSlabTable) {
    const from = Number(slab.incomeFrom) || 0;
    const to = slab.incomeTo == null ? Infinity : Number(slab.incomeTo);
    const rate = Number(slab.taxRatePercent) || 0;
    if (income <= from) continue;
    const taxableInSlab = Math.min(income, to) - from;
    if (taxableInSlab > 0) totalTax += (taxableInSlab * rate) / 100;
  }
  return totalTax;
}

// ---------------------------------------------------------------------------
// computeTds
// ---------------------------------------------------------------------------

export function computeTds(gross, monthInFY, tdsSlabTable) {
  if (!tdsSlabTable || tdsSlabTable.length === 0) return 0;
  const g = Number(gross) || 0;
  const m = Number(monthInFY) || 1;
  const annualTax = computeAnnualTax(g * 12, tdsSlabTable);
  if (annualTax === 0) return 0;
  return Math.round(annualTax / (13 - m));
}

// ---------------------------------------------------------------------------
// computeNet
// ---------------------------------------------------------------------------

export function computeNet(gross, pfEmployee, esic, pt, tds, lop, customDeductions) {
  const customTotal = Array.isArray(customDeductions)
    ? customDeductions.reduce((sum, d) => sum + (Number(d) || 0), 0)
    : 0;
  return Math.max(
    0,
    (Number(gross) || 0) -
      (Number(pfEmployee) || 0) -
      (Number(esic) || 0) -
      (Number(pt) || 0) -
      (Number(tds) || 0) -
      (Number(lop) || 0) -
      customTotal,
  );
}

// ---------------------------------------------------------------------------
// computePayrollPreview — org-wide model
// ---------------------------------------------------------------------------

export function computePayrollPreview(template, inputs, slabs) {
  const earningComponents = template?.earningComponents || [];
  const deductionComponents = template?.deductionComponents || {};

  const monthlySalary = Number(inputs?.monthlySalary) || 0;
  const presentDays = Number(inputs?.presentDays) || 0;
  const monthDays = Number(inputs?.monthDays) || 26;
  const absentDays = Math.max(0, monthDays - presentDays);

  const resolved = resolveComponentsWithSalary(earningComponents, monthlySalary);

  const earnings = earningComponents.map((comp) => {
    const fullAmount = resolved.get(comp.id) || 0;
    const isProrata = comp.isProrata !== false;
    const amount = computeProrata(fullAmount, presentDays, monthDays, isProrata);
    return {
      id: comp.id,
      name: comp.name || "",
      fullAmount,
      amount,
      isProrata,
      isResidual: !!(comp.isResidual || comp.calculationType === "residual"),
    };
  });

  const grossSalary = earnings.reduce((sum, e) => sum + e.amount, 0);
  const lopAmount = Math.max(0, monthlySalary - grossSalary);

  const basicComp =
    earningComponents.find((c) => c.name && c.name.toLowerCase().includes("basic")) ||
    earningComponents.find((c) => c.calculationType === "fixed");
  const basicAmount = basicComp
    ? earnings.find((e) => e.id === basicComp.id)?.amount || 0
    : grossSalary;

  const pfConfig = deductionComponents?.pf || {};
  let pfEmployee = 0,
    pfEmployer = 0;
  if (pfConfig.enabled) {
    const pfResult = computePf(
      basicAmount,
      pfConfig.employeePercent ?? 12,
      pfConfig.employerPercent ?? 12,
      pfConfig.ceiling ?? 15000,
    );
    pfEmployee = pfResult.employee;
    pfEmployer = pfResult.employer;
  }

  const esicConfig = deductionComponents?.esic || {};
  const esic = esicConfig.enabled
    ? computeEsic(
        grossSalary,
        esicConfig.eligibilityCeiling ?? 21000,
        esicConfig.employeePercent ?? 0.75,
      )
    : 0;

  const ptConfig = deductionComponents?.pt || {};
  const pt = ptConfig.enabled
    ? computePt(grossSalary, slabs?.state || "preview", slabs?.ptSlabs || [])
    : 0;

  const tdsConfig = deductionComponents?.tds || {};
  const tds = tdsConfig.enabled
    ? computeTds(grossSalary, slabs?.monthInFY || 1, slabs?.tdsSlabs || [])
    : 0;

  const customList = Array.isArray(deductionComponents?.custom) ? deductionComponents.custom : [];
  const customAmounts = customList.map((d) =>
    d.calculationType === "percentage_of_gross"
      ? Math.round((grossSalary * (Number(d.percentage) || 0)) / 100)
      : Number(d.fixedAmount) || 0,
  );

  const netSalary = computeNet(grossSalary, pfEmployee, esic, pt, tds, 0, customAmounts);
  const totalDeductions = pfEmployee + esic + pt + tds + customAmounts.reduce((s, a) => s + a, 0);

  return {
    monthlySalary,
    presentDays,
    monthDays,
    absentDays,
    earnings,
    grossSalary,
    lopAmount,
    pfEmployee,
    pfEmployer,
    esic,
    pt,
    tds,
    customDeductions: customList.map((d, i) => ({ name: d.name, amount: customAmounts[i] })),
    totalDeductions,
    netSalary,
  };
}

// ---------------------------------------------------------------------------
// computeAllocation
// ---------------------------------------------------------------------------

export function computeAllocation(components, referenceSalary) {
  const salary = Number(referenceSalary) || 50000;
  const resolved = resolveComponentsWithSalary(components, salary);

  const hasResidual = components.some((c) => c.calculationType === "residual" || c.isResidual);

  const nonResidualComponents = components.filter(
    (c) => c.calculationType !== "residual" && !c.isResidual,
  );

  const allocatedAmount = nonResidualComponents.reduce(
    (sum, c) => sum + (resolved.get(c.id) || 0),
    0,
  );

  const residualAmount = Math.max(0, salary - allocatedAmount);
  const totalPercent = salary > 0 ? (allocatedAmount / salary) * 100 : 0;
  const isBalanced = hasResidual || Math.abs(totalPercent - 100) < 0.01;

  return { totalPercent, allocatedAmount, residualAmount, isBalanced, hasResidual };
}

// ---------------------------------------------------------------------------
// buildPayslipData — legacy (used by existing payslip preview)
// ---------------------------------------------------------------------------

export function buildPayslipData(template, attendance, slabs, employeeInfo) {
  const earningComponents = template?.earningComponents || [];
  const deductionComponents = template?.deductionComponents || {};

  const resolved = resolveComponents(earningComponents);
  const earnings = earningComponents.map((comp) => ({
    name: comp.name || "",
    amount: resolved.get(comp.id) || 0,
  }));
  const grossSalary = earnings.reduce((sum, e) => sum + e.amount, 0);

  const basicComp =
    earningComponents.find((c) => c.name && c.name.toLowerCase() === "basic") ||
    earningComponents.find((c) => c.calculationType === "fixed");
  const basicAmount = basicComp ? resolved.get(basicComp.id) || 0 : grossSalary;

  const workingDays = Number(attendance?.workingDays) || 0;
  const absentDays = Number(attendance?.absentDays) || 0;
  const lopAmount = computeLop(grossSalary, workingDays, absentDays);

  const pfConfig = deductionComponents?.pf || {};
  let pfEmployee = 0,
    pfEmployer = 0;
  if (pfConfig.enabled === true) {
    const r = computePf(
      basicAmount,
      pfConfig.employeePercent ?? 12,
      pfConfig.employerPercent ?? 12,
    );
    pfEmployee = r.employee;
    pfEmployer = r.employer;
  }

  const esicConfig = deductionComponents?.esic || {};
  const esic =
    esicConfig.enabled === true
      ? computeEsic(
          grossSalary,
          esicConfig.eligibilityCeiling ?? 21000,
          esicConfig.employeePercent ?? 0.75,
        )
      : 0;

  const ptConfig = deductionComponents?.pt || {};
  const pt =
    ptConfig.enabled === true
      ? computePt(grossSalary, employeeInfo?.state || "", slabs?.ptSlabs || [])
      : 0;

  const tdsConfig = deductionComponents?.tds || {};
  const tds =
    tdsConfig.enabled === true
      ? computeTds(grossSalary, slabs?.monthInFY || 1, slabs?.tdsSlabs || [])
      : 0;

  const customDeductionList = Array.isArray(deductionComponents?.custom)
    ? deductionComponents.custom
    : [];
  const customDeductionAmounts = customDeductionList.map((d) =>
    d.calculationType === "percentage_of_gross"
      ? Math.round((grossSalary * (Number(d.percentage) || 0)) / 100)
      : Number(d.fixedAmount) || 0,
  );

  const deductions = [];
  if (pfConfig.enabled === true) deductions.push({ name: "PF (Employee)", amount: pfEmployee });
  if (esicConfig.enabled === true) {
    deductions.push({
      name:
        grossSalary > (esicConfig.eligibilityCeiling ?? 21000)
          ? "ESIC (not applicable — gross exceeds ceiling)"
          : "ESIC",
      amount: esic,
    });
  }
  if (ptConfig.enabled === true && pt > 0)
    deductions.push({ name: "Professional Tax", amount: pt });
  if (tdsConfig.enabled === true && tds > 0) deductions.push({ name: "TDS", amount: tds });
  if (lopAmount > 0) deductions.push({ name: "Loss of Pay", amount: lopAmount });
  customDeductionList.forEach((d, i) => {
    const amount = customDeductionAmounts[i];
    if (amount > 0) deductions.push({ name: d.name || "Custom Deduction", amount });
  });

  const netSalary = computeNet(
    grossSalary,
    pfEmployee,
    esic,
    pt,
    tds,
    lopAmount,
    customDeductionAmounts,
  );
  const totalDeductions = deductions.reduce((sum, d) => sum + d.amount, 0);

  return {
    companyName: employeeInfo?.companyName || "",
    companyLogo: employeeInfo?.companyLogo || "",
    companyAddress: employeeInfo?.companyAddress || "",
    employeeName: employeeInfo?.staffName || "",
    employeeId: employeeInfo?.employeeId || "",
    designation: employeeInfo?.designation || "",
    department: employeeInfo?.department || "",
    payrollMonth: employeeInfo?.payrollMonth || "",
    earnings,
    deductions,
    grossSalary,
    totalDeductions,
    netSalary,
    employerPf: pfEmployer,
    lopAmount,
    pfEmployee,
    pfEmployer,
    esic,
    pt,
    tds,
  };
}

// ---------------------------------------------------------------------------
// Guard functions
// ---------------------------------------------------------------------------

export function canActivate(template) {
  if (!template) return false;
  const components = template.earningComponents;
  if (!Array.isArray(components) || components.length === 0) return false;
  return true;
}

export function canEditBatch(batch) {
  if (!batch) return false;
  return batch.status !== "Paid";
}

export function canInitiateRun(month, year, existingBatches) {
  if (!Array.isArray(existingBatches) || existingBatches.length === 0) return true;
  const m = Number(month);
  const y = Number(year);
  return !existingBatches.some((b) => Number(b.month) === m && Number(b.year) === y);
}
